import type { SanityImageSource } from '../interfaces'
import { SANITY_IMAGE_CDN_BASE } from './sanityClient'

// Vendored replacement for @sanity/image-url (which requires Node >= 20 to
// install). It only covers what the Resource Center needs: turning an image
// asset reference into a CDN URL with basic resize params.

export interface SanityImageUrlOptions {
  width?: number
  height?: number
  fit?: 'crop' | 'max'
  auto?: 'format'
}

// Sanity asset _ref format: image-<assetId>-<width>x<height>-<format>
// e.g. image-abc123def-1920x1080-jpg
const IMAGE_REF_PATTERN = /^image-([A-Za-z0-9]+)-(\d+x\d+)-([a-z0-9]+)$/

const getAssetRef = (source: SanityImageSource): string | null => {
  return source.asset?._ref || source._ref || null
}

export const buildSanityImageUrl = (
  source: SanityImageSource | null | undefined,
  options: SanityImageUrlOptions = {},
): string | null => {
  const ref = source ? getAssetRef(source) : null
  const match = ref ? ref.match(IMAGE_REF_PATTERN) : null
  if (!match) return null

  const [, assetId, dimensions, format] = match
  const baseUrl = `${SANITY_IMAGE_CDN_BASE}/${assetId}-${dimensions}.${format}`

  const params = new URLSearchParams()
  if (options.width) params.set('w', String(options.width))
  if (options.height) params.set('h', String(options.height))
  if (options.fit) params.set('fit', options.fit)
  if (options.auto) params.set('auto', options.auto)

  const query = params.toString()
  return query ? `${baseUrl}?${query}` : baseUrl
}
